import React, { useState, useEffect } from 'react';
import './IndirectMethodSummary.css';
import { googleFormsService } from '../services/googleFormsService';
import { openAIService } from '../services/openAIService';
import IndirectMethod_RecentSurveyHistory_SurveyResult from './IndirectMethod_RecentSurveyHistory_SurveyResult';
import IndirectMethod_ImportSurveysModal from './IndirectMethod_ImportSurveysModal';

const IndirectMethodSummary = ({ programName, school }) => {
  //console.log('📊 Program Name received:', programName);
  //console.log('🏫 School received:', school);
  
  // Robust program name parsing function
  const getProgramDisplayName = (programField) => {
    if (!programField || typeof programField !== 'string') return programField;
    
    // Check if it looks like JSON (course mapping data)
    if (programField.startsWith('{') && programField.endsWith('}')) {
      try {
        // Try parsing as JSON (handle both single and double quotes)
        const courseData = JSON.parse(programField.replace(/'/g, '"')); 
        return courseData.course_name || programField;
      } catch (e) {
        // Try regex fallback for malformed JSON
        const match = programField.match(/course_name['":\s]*([^'",}]+)/);
        return match ? match[1].replace(/['"]/g, '') : programField;
      }
    }
    
    // Return as-is for normal strings (PSC, BSM, Batch Processing, etc.)
    return programField;
  };

  // Format detection function for survey analysis data
  const detectDataFormat = (analysisResults) => {
    // Check for new format indicators
    const hasNewFormat = analysisResults?.distribution && analysisResults?.average_scores;
    
    // Check for old format indicators  
    const hasOldFormat = analysisResults?.questions && analysisResults?.overall_summary;
    
    if (hasNewFormat) return 'new';
    if (hasOldFormat) return 'old';
    return 'unknown';
  };

  // Response count extraction function for both data formats
  const getResponseCount = (analysis) => {
    const analysisResults = analysis.analysis_results;
    const format = detectDataFormat(analysisResults);
    
    let responseCount = 'N/A';
    
    try {
      if (format === 'new') {
        // New format: Extract from average_scores.averages.[firstQuestion].total_count
        const averages = analysisResults?.average_scores?.averages;
        if (averages) {
          const firstQuestionKey = Object.keys(averages)[0];
          if (firstQuestionKey) {
            responseCount = averages[firstQuestionKey]?.total_count || 'N/A';
          }
        }
      } else if (format === 'old') {
        // Old format: Use existing logic (questions[0].response_count)
        responseCount = analysisResults?.questions?.[0]?.response_count || 'N/A';
      }
    } catch (error) {
      console.error('🚨 Error extracting response count:', error);
      responseCount = 'N/A';
    }
    
    return responseCount;
  };

  // Question count extraction function for both data formats
  const getQuestionCount = (analysis) => {
    const analysisResults = analysis.analysis_results;
    const format = detectDataFormat(analysisResults);
    
    let questionCount = 0;
    
    try {
      if (format === 'new') {
        // New format: Count questions from distribution object keys
        const distribution = analysisResults?.distribution;
        if (distribution && typeof distribution === 'object') {
          questionCount = Object.keys(distribution).length;
        }
      } else if (format === 'old') {
        // Old format: Count questions from questions array
        const questions = analysisResults?.questions;
        if (questions && Array.isArray(questions)) {
          questionCount = questions.length;
        }
      }
    } catch (error) {
      console.error('🚨 Error extracting question count:', error);
      questionCount = 0;
    }
    
    return questionCount;
  };

  // Success message data extraction function for both data formats
  const getSuccessMessageData = (result) => {
    if (!result.analysisData) {
      return { message: result.message || 'Import completed successfully' };
    }
    
    const format = detectDataFormat(result.analysisData);
    let messageData = { message: result.message || 'Import completed successfully' };
    
    try {
      if (format === 'new') {
        // New format: Extract from average_scores and distribution
        const averageScores = result.analysisData?.average_scores;
        const distribution = result.analysisData?.distribution;
        
        if (averageScores && distribution) {
          const questionCount = Object.keys(distribution).length;
          const overallAverage = averageScores.overall_average || 0;
          
          // Get response count from first question
          const averages = averageScores.averages;
          let responseCount = 0;
          if (averages) {
            const firstQuestionKey = Object.keys(averages)[0];
            if (firstQuestionKey) {
              responseCount = averages[firstQuestionKey]?.total_count || 0;
            }
          }
          
          messageData = {
            message: `${result.message || 'Import completed successfully'} Found ${questionCount} questions with ${responseCount} responses. Overall average: ${overallAverage.toFixed(1)}/5.0`,
            questionCount,
            responseCount,
            overallAverage
          };
        }
      } else if (format === 'old') {
        // Old format: Use existing overall_summary logic
        const summary = result.analysisData.overall_summary;
        if (summary) {
          messageData = {
            message: `${result.message || 'Import completed successfully'} Found ${summary.total_questions} questions with ${summary.total_responses} responses. Overall average: ${summary.overall_average}/5.0`,
            questionCount: summary.total_questions,
            responseCount: summary.total_responses,
            overallAverage: summary.overall_average
          };
        }
      }
    } catch (error) {
      console.error('🚨 Error extracting success message data:', error);
      messageData = { message: result.message || 'Import completed successfully' };
    }
    
    return messageData;
  };

  // State for active surveys
  const [activeSurveys, setActiveSurveys] = useState([]);

  // State for recent survey history
  const [recentSurveys, setRecentSurveys] = useState([]);

  // State for notifications and loading
  const [isCreating, setIsCreating] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoadingSurveys, setIsLoadingSurveys] = useState(true);

  // State for dropdown menus
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // State for Recent Survey History dropdown (separate from Active Surveys)
  const [historyDropdown, setHistoryDropdown] = useState(null);
  const [historyDropdownPosition, setHistoryDropdownPosition] = useState({ top: 0, left: 0 });

  // State for delete confirmation modal
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [surveyToDelete, setSurveyToDelete] = useState(null);
  const [isDeletingInProgress, setIsDeletingInProgress] = useState(false);

  // State for view management
  const [currentView, setCurrentView] = useState('list'); // 'list' or 'survey-results'
  const [selectedSurvey, setSelectedSurvey] = useState(null);

  // State for Import Surveys modal
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  // Fetch active surveys on component mount
  useEffect(() => {
    fetchActiveSurveys();
    fetchAnalyses(); // Also fetch imported survey analyses
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.indirect_method_dropdown_container')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  // Update Active Surveys dropdown position on scroll (RAF-based)
  useEffect(() => {
    let rafId = null;

    const handleActiveScroll = () => {
      if (!openDropdown || rafId) return;
      
      rafId = requestAnimationFrame(() => {
        const buttonElement = document.querySelector(`[data-survey-id="${openDropdown}"]`);
        if (buttonElement) {
          // Recalculate position using same logic as handleDropdownToggle
          const rect = buttonElement.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
          
          setDropdownPosition({
            top: rect.bottom + scrollTop + 4, // 4px gap below button
            left: rect.right + scrollLeft - 180 // 180px dropdown width, align right
          });
        }
        rafId = null;
      });
    };

    if (openDropdown) {
      // Listen to both window scroll and any container scrolls
      window.addEventListener('scroll', handleActiveScroll, { passive: true, capture: true });
      document.addEventListener('scroll', handleActiveScroll, { passive: true, capture: true });
    }

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      window.removeEventListener('scroll', handleActiveScroll, { capture: true });
      document.removeEventListener('scroll', handleActiveScroll, { capture: true });
    };
  }, [openDropdown]);

  // Close Recent Survey History dropdown when clicking outside
  useEffect(() => {
    const handleHistoryClickOutside = (event) => {
      if (historyDropdown && !event.target.closest('.indirect_method_dropdown_container')) {
        setHistoryDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleHistoryClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleHistoryClickOutside);
    };
  }, [historyDropdown]);

  // Update Recent Survey History dropdown position on scroll (RAF-based)
  useEffect(() => {
    let rafId = null;

    const handleHistoryScroll = () => {
      if (!historyDropdown || rafId) return;
      
      rafId = requestAnimationFrame(() => {
        const buttonElement = document.querySelector(`[data-history-survey-id="${historyDropdown}"]`);
        if (buttonElement) {
          // Recalculate position using same logic as handleHistoryDropdownToggle
          const rect = buttonElement.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
          
          setHistoryDropdownPosition({
            top: rect.bottom + scrollTop + 4, // 4px gap below button
            left: rect.right + scrollLeft - 180 // 180px dropdown width, align right
          });
        }
        rafId = null;
      });
    };

    if (historyDropdown) {
      // Listen to both window scroll and any container scrolls
      window.addEventListener('scroll', handleHistoryScroll, { passive: true, capture: true });
      document.addEventListener('scroll', handleHistoryScroll, { passive: true, capture: true });
    }

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      window.removeEventListener('scroll', handleHistoryScroll, { capture: true });
      document.removeEventListener('scroll', handleHistoryScroll, { capture: true });
    };
  }, [historyDropdown]);

  const fetchActiveSurveys = async () => {
    try {
      setIsLoadingSurveys(true);
      
      const surveys = await googleFormsService.getActiveSurveys(school);
      setActiveSurveys(surveys);
    } catch (error) {
      console.error('Failed to fetch surveys:', error);
      // Keep empty array on error, will show "No surveys" message
      setActiveSurveys([]);
    } finally {
      setIsLoadingSurveys(false);
    }
  };

  // Fetch imported survey analyses
  const fetchAnalyses = async () => {
    try {
      const analyses = await openAIService.listAnalyses(school);
      
      // Convert analyses to survey format for Recent Survey History
      const formattedAnalyses = analyses
        .filter(analysis => analysis.status === 'completed')
        .map(analysis => {
          return {
            id: `analysis_${analysis.id}`,
            name: analysis.original_filename.replace(/\.(xlsx|xls)$/i, ''),
            program: analysis.program || programName,          
            // OLD LOGIC (commented out for easy rollback):
            // responses: analysis.analysis_results?.overall_summary?.total_responses || 'N/A',
            // PREVIOUS LOGIC (commented out for easy rollback):
            // responses: analysis.analysis_results?.questions?.[0]?.response_count || 'N/A',
            // NEW LOGIC - Use adapter function for both formats:
            responses: getResponseCount(analysis),
            status: 'Completed',
            analysisData: analysis.analysis_results,
            analysisId: analysis.id,
            isImported: true // Flag to distinguish from Google Forms surveys
          };
        });
      
      // Set only real imported surveys (no mock data)
      setRecentSurveys(formattedAnalyses);
      
    } catch (error) {
      console.error('Failed to fetch analyses:', error);
      // Set empty array on error
      setRecentSurveys([]);
    }
  };

  // Handle creating new survey
  const handleCreateNewSurvey = async () => {
    if (isCreating) return; // Prevent multiple clicks

    setIsCreating(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      // Check if Google Identity Services is loaded
      if (!window.google || !window.google.accounts) {
        throw new Error('Google Identity Services not loaded');
      }

      //console.log('🔐 Starting Google authentication...');

      // Debug: Check if client ID is loaded
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      //console.log('🔑 Client ID loaded:', clientId);
      //console.log('🔑 Client ID length:', clientId?.length);
      //console.log('🔑 Client ID ends with .apps.googleusercontent.com:', clientId?.endsWith('.apps.googleusercontent.com'));

      // Initialize Google OAuth client
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId || 'YOUR_GOOGLE_CLIENT_ID_HERE',
        scope: 'https://www.googleapis.com/auth/forms.body',
        callback: async (tokenResponse) => {
          try {
            //console.log('✅ Google auth successful, creating form...');
            
            // Prepare metadata to send to backend
            const metadata = {
              program: programName,  // "BSM" 
              school: school         // "MARYLAND"
            };
            //console.log('📊 Sending metadata:', metadata);
            
            // Create form with user's access token and metadata
            const result = await googleFormsService.createForm(tokenResponse.access_token, metadata);
            
            // Try to open in new tab
            const newTab = window.open(result.editUrl, '_blank', 'noopener,noreferrer');
            
            if (newTab && !newTab.closed) {
              // Success! New tab opened
              setSuccessMessage('Survey Created Successfully!');
            } else {
              // Failed to open, provide manual link
              setSuccessMessage(
                <span>
                  Survey Created Successfully! 
                  <a 
                    href={result.editUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    style={{
                      color: '#3b82f6', 
                      textDecoration: 'underline', 
                      marginLeft: '8px',
                      fontWeight: '500'
                    }}
                  >
                    Click here to open your survey →
                  </a>
                </span>
              );
            }
            
            // Refresh the survey list to show the new survey
            fetchActiveSurveys();
            
            // Clear success message after 8 seconds (longer for manual link)
            setTimeout(() => setSuccessMessage(''), 8000);
            
          } catch (error) {
            console.error('Error in Google auth callback:', error);
            setErrorMessage('Failed to create survey after authentication. Please try again.');
            setTimeout(() => setErrorMessage(''), 5000);
          } finally {
            setIsCreating(false);
          }
        },
        error_callback: (error) => {
          console.error('Google auth error:', error);
          setErrorMessage('Google authentication failed. Please try again.');
          setTimeout(() => setErrorMessage(''), 5000);
          setIsCreating(false);
        }
      });

      // Request access token
      client.requestAccessToken();
      
    } catch (error) {
      console.error('Error initializing Google auth:', error);
      setErrorMessage('Failed to initialize Google authentication. Please try again.');
      setTimeout(() => setErrorMessage(''), 5000);
      setIsCreating(false);
    }
  };

  // Handle viewing survey responses
  const handleViewResponses = (survey) => {
    if (!survey.form_id) {
      console.error('No form ID available for survey:', survey);
      setErrorMessage('Unable to view responses - form ID not found.');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    try {
      // Construct Google Forms response URL (using edit interface with responses tab)
      const responseUrl = `https://docs.google.com/forms/d/${survey.form_id}/edit?pli=1#responses`;
      //console.log('🔗 Opening response URL:', responseUrl);
      
      // Open Google Forms response page in new tab
      const newTab = window.open(responseUrl, '_blank', 'noopener,noreferrer');
      
      if (!newTab || newTab.closed) {
        // Fallback if popup blocked
        setErrorMessage(
          <span>
            Popup blocked. 
            <a 
              href={responseUrl} 
              target="_blank" 
              rel="noopener noreferrer" 
              style={{
                color: '#3b82f6', 
                textDecoration: 'underline', 
                marginLeft: '8px',
                fontWeight: '500'
              }}
            >
              Click here to view responses →
            </a>
          </span>
        );
        setTimeout(() => setErrorMessage(''), 5000);
      }
    } catch (error) {
      console.error('Error opening response page:', error);
      setErrorMessage('Failed to open response page. Please try again.');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  // Handle dropdown toggle
  const handleDropdownToggle = (surveyId, event) => {
    if (openDropdown === surveyId) {
      setOpenDropdown(null);
      return;
    }

    // Calculate position for fixed positioning
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    
    setDropdownPosition({
      top: rect.bottom + scrollTop + 4, // 4px gap below button
      left: rect.right + scrollLeft - 180 // 180px is dropdown width, align right
    });
    
    setOpenDropdown(surveyId);
  };

  // Handle Recent Survey History dropdown toggle (identical logic to Active Surveys)
  const handleHistoryDropdownToggle = (surveyId, event) => {
    if (historyDropdown === surveyId) {
      setHistoryDropdown(null);
      return;
    }

    // Calculate position for fixed positioning (identical to Active Surveys)
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    
    setHistoryDropdownPosition({
      top: rect.bottom + scrollTop + 4, // 4px gap below button
      left: rect.right + scrollLeft - 180 // 180px is dropdown width, align right
    });
    
    setHistoryDropdown(surveyId);
  };

  // Handle refresh responses (placeholder for now)
  const handleRefreshResponses = (survey) => {
    //console.log('🔄 Refreshing responses for survey:', survey.name);
    setOpenDropdown(null); // Close dropdown
    // TODO: Implement actual refresh logic
    setSuccessMessage('Response refresh functionality coming soon!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Handle delete survey from Recent Survey History
  const handleDeleteSurvey = (survey) => {
    setHistoryDropdown(null); // Close dropdown immediately
    setSurveyToDelete(survey);
    setShowDeleteConfirmation(true);
  };

  // Handle confirmation of delete
  const handleConfirmDelete = async () => {
    if (!surveyToDelete) return;
    
    // 🔍 Extract and validate analysis ID with fallback
    let analysisId = surveyToDelete.analysisId;
    
    // Fallback: Extract numeric ID from prefixed string if analysisId is undefined
    if (!analysisId && surveyToDelete.id) {
      const extractedId = surveyToDelete.id.replace('analysis_', '');
      analysisId = parseInt(extractedId, 10);
      console.warn('⚠️ Using fallback ID extraction:', { original: surveyToDelete.id, extracted: analysisId });
    }
    
    // Validation: Ensure we have a valid numeric ID
    if (!analysisId || typeof analysisId !== 'number' || isNaN(analysisId)) {
      console.error('❌ Invalid analysis ID:', { 
        analysisId, 
        type: typeof analysisId, 
        surveyId: surveyToDelete.id,
        surveyAnalysisId: surveyToDelete.analysisId 
      });
      setErrorMessage('Unable to delete survey: Invalid survey ID');
      setIsDeletingInProgress(false);
      setShowDeleteConfirmation(false);
      setSurveyToDelete(null);
      return;
    }
    
    // 📊 Production logging for troubleshooting
    console.log('🗑️ Deleting survey:', { 
      surveyName: surveyToDelete.name, 
      analysisId, 
      school,
      timestamp: new Date().toISOString() 
    });
    
    setIsDeletingInProgress(true);
    
    try {
      // Call the delete API with validated numeric ID
      await openAIService.deleteAnalysis(analysisId, {
        school: school // Pass school parameter as required by backend
      });
      
      // Remove from UI only after successful deletion
      setRecentSurveys(prevSurveys => 
        prevSurveys.filter(survey => survey.id !== surveyToDelete.id)
      );
      
      // Show success message
      setSuccessMessage(`Survey "${surveyToDelete.name}" deleted successfully`);
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (error) {
      // Keep item in UI, show error message
      console.error('Delete failed:', error);
      setErrorMessage('The deletion fails. Try again later.');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      // Close modal and reset state
      setIsDeletingInProgress(false);
      setShowDeleteConfirmation(false);
      setSurveyToDelete(null);
    }
  };

  // Handle cancellation of delete
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setSurveyToDelete(null);
    setIsDeletingInProgress(false);
  };

  // Handle viewing survey results from Recent Survey History
  const handleViewSurveyResults = (survey) => {
    setSelectedSurvey(survey);
    setCurrentView('survey-results');
  };

  // Handle back to survey list
  const handleBackToSurveyList = () => {
    setCurrentView('list');
    setSelectedSurvey(null);
  };

  // Handle opening Import Surveys modal
  const handleImportSurveysClick = () => {
    setIsImportModalOpen(true);
  };

  // Handle closing Import Surveys modal
  const handleImportModalClose = () => {
    setIsImportModalOpen(false);
    
    // ✅ OPTION C FIX: Always refresh analyses when modal closes
    // This ensures that any successful uploads from previous sessions
    // (before "Upload More Files" was clicked) remain visible in Recent Survey History
    // even if the user cancels the additional upload attempt
    fetchAnalyses();
  };

  // Handle successful upload
  const handleImportSuccess = (result) => {
    //console.log('Import successful:', result);
    
    // Show success message with more details
    const extractedData = getSuccessMessageData(result);
    setSuccessMessage(extractedData.message);
    
    setIsImportModalOpen(false);
    
    // Refresh analyses to show the new import in Recent Survey History
    fetchAnalyses();
    
    setTimeout(() => setSuccessMessage(''), 8000);
  };

  // If viewing survey results, render the results component
  if (currentView === 'survey-results') {
    return (
      <IndirectMethod_RecentSurveyHistory_SurveyResult 
        survey={selectedSurvey}
        onBack={handleBackToSurveyList}
      />
    );
  }

  return (
    <div className="indirect_method_container">
      {/* Notification Messages */}
      {successMessage && (
        <div className="indirect_method_notification indirect_method_success">
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="indirect_method_notification indirect_method_error">
          {errorMessage}
        </div>
      )}

      {/* Survey Creation Cards */}
      <div className="indirect_method_creation_section">
        <div className="indirect_method_creation_cards">
          <div 
            className={`indirect_method_card indirect_method_create_new ${isCreating ? 'indirect_method_card_loading' : ''}`}
            onClick={handleCreateNewSurvey}
            style={{ cursor: isCreating ? 'not-allowed' : 'pointer' }}
          >
            <div className="indirect_method_card_icon">
              <div className="indirect_method_checkmark_icon">
                {isCreating ? '⏳' : '✓'}
              </div>
            </div>
            <div className="indirect_method_card_content">
              <h3 className="indirect_method_card_title">
                {isCreating ? 'Creating Survey...' : 'Create New Survey'}
              </h3>
              <p className="indirect_method_card_description">Craft questions by yourself</p>
            </div>
          </div>

          <div 
            className="indirect_method_card indirect_method_import_surveys"
            onClick={handleImportSurveysClick}
            style={{ cursor: 'pointer' }}
          >
            <div className="indirect_method_card_icon">
              <div className="indirect_method_import_icon">📥</div>
            </div>
            <div className="indirect_method_card_content">
              <h3 className="indirect_method_card_title">Import Surveys</h3>
              <p className="indirect_method_card_description">Import Surveys from External Sources</p>
            </div>
          </div>
        </div>
      </div>

      {/* Active Surveys Section */}
      <div className="indirect_method_surveys_section">
        <h2 className="indirect_method_section_title">Active Surveys</h2>
        
        <div className="indirect_method_table_container">
          <table className="indirect_method_table">
            <thead>
              <tr>
                <th className="indirect_method_th_left">Survey Name</th>
                <th className="indirect_method_th_center">Program/Course</th>
                <th className="indirect_method_th_center">Responses</th>
                <th className="indirect_method_th_center">Status</th>
                <th className="indirect_method_th_center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingSurveys ? (
                <tr>
                  <td colSpan="5" className="indirect_method_td_center">
                    Loading surveys...
                  </td>
                </tr>
              ) : activeSurveys.length === 0 ? (
                <tr>
                  <td colSpan="5" className="indirect_method_td_center">
                    No surveys created yet. Create your first survey above!
                  </td>
                </tr>
              ) : (
                activeSurveys.map((survey) => (
                  <tr key={survey.id} className="indirect_method_table_row">
                    <td className="indirect_method_td_left">{survey.name}</td>
                    <td className="indirect_method_td_center">{getProgramDisplayName(survey.program)}</td>
                    <td className="indirect_method_td_center">{survey.responses}</td>
                    <td className="indirect_method_td_center">
                      <span className={`indirect_method_status_badge indirect_method_status_${survey.status.toLowerCase()}`}>
                        {survey.status}
                      </span>
                    </td>
                    <td className="indirect_method_td_center">
                      <div className="indirect_method_actions">
                        <button 
                          className="indirect_method_action_button indirect_method_view_button"
                          onClick={() => handleViewResponses(survey)}
                          title="View survey responses in Google Forms"
                        >
                          View
                        </button>
                        <div className="indirect_method_dropdown_container">
                          <button 
                            className="indirect_method_action_button indirect_method_more_button"
                            data-survey-id={survey.id}
                            onClick={(event) => handleDropdownToggle(survey.id, event)}
                            title="More actions"
                          >
                            ⋯
                          </button>
                          {openDropdown === survey.id && (
                            <div className="indirect_method_dropdown_menu" style={dropdownPosition}>
                              <button 
                                className="indirect_method_dropdown_item"
                                onClick={() => handleRefreshResponses(survey)}
                              >
                                <span className="indirect_method_dropdown_icon">🔄</span>
                                Refresh Responses
                              </button>
                              <button 
                                className="indirect_method_dropdown_item"
                                onClick={() => {
                                  //console.log('Edit survey:', survey.name);
                                  setOpenDropdown(null);
                                }}
                              >
                                <span className="indirect_method_dropdown_icon">✏️</span>
                                Edit Survey
                              </button>
                              <button 
                                className="indirect_method_dropdown_item"
                                onClick={() => {
                                  //console.log('Copy link:', survey.name);
                                  setOpenDropdown(null);
                                }}
                              >
                                <span className="indirect_method_dropdown_icon">📋</span>
                                Copy Link
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Survey History Section */}
      <div className="indirect_method_history_section">
        <h2 className="indirect_method_section_title">Recent Survey History</h2>
        
        {/* Template Download Info Box */}
        <div className="indirect_method_template_info_box">
          <div className="indirect_method_template_info_content">
            <span className="indirect_method_template_icon">📋</span>
            <div className="indirect_method_template_text">
              <span className="indirect_method_template_description">
                For the best analysis result, using this template to summarize data is strongly recommended.
              </span>
              <a 
                href="/template/SurveyTemplate.xlsx" 
                download 
                className="indirect_method_template_download_btn"
              >
                <span className="indirect_method_template_download_icon">⬇️</span>
                Download Template
              </a>
            </div>
          </div>
        </div>
        
        <div className="indirect_method_table_container">
          <table className="indirect_method_table">
            <thead>
              <tr>
                <th className="indirect_method_th_left">Survey Name</th>
                <th className="indirect_method_th_center">Program/Course</th>
                <th className="indirect_method_th_center">Responses</th>
                <th className="indirect_method_th_center">Status</th>
                <th className="indirect_method_th_center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {recentSurveys.map((survey) => (
                <tr key={survey.id} className="indirect_method_table_row">
                  <td className="indirect_method_td_left">{survey.name}</td>
                  <td className="indirect_method_td_center">{getProgramDisplayName(survey.program)}</td>
                  <td className="indirect_method_td_center">{survey.responses}</td>
                  <td className="indirect_method_td_center">
                    <span className={`indirect_method_status_badge indirect_method_status_${survey.status.toLowerCase()}`}>
                      {survey.status}
                    </span>
                  </td>
                  <td className="indirect_method_td_center">
                    <div className="indirect_method_actions">
                      <button 
                        className="indirect_method_action_button indirect_method_view_button"
                        onClick={() => handleViewSurveyResults(survey)}
                        title="View survey results"
                      >
                        View
                      </button>
                      <div className="indirect_method_dropdown_container">
                        <button 
                          className="indirect_method_action_button indirect_method_more_button"
                          data-history-survey-id={survey.id}
                          onClick={(event) => handleHistoryDropdownToggle(survey.id, event)}
                          title="More actions"
                        >
                          ⋯
                        </button>
                        {historyDropdown === survey.id && (
                          <div className="indirect_method_dropdown_menu" style={historyDropdownPosition}>
                            <button 
                              className="indirect_method_dropdown_item"
                              onClick={() => handleDeleteSurvey(survey)}
                            >
                              <span className="indirect_method_dropdown_icon">🗑️</span>
                              Delete Survey
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="indirect_method_modal_overlay">
          <div className="indirect_method_modal_content">
            <div className="indirect_method_modal_header">
              <h3 className="indirect_method_modal_title">Delete Survey</h3>
            </div>
            
            <div className="indirect_method_modal_body">
              <div className="indirect_method_delete_warning">
                <div className="indirect_method_warning_icon">⚠️</div>
                <div className="indirect_method_warning_content">
                  <p className="indirect_method_warning_text">
                    Are you sure you want to delete <strong>"{surveyToDelete?.name}"</strong>?
                  </p>
                  <p className="indirect_method_warning_subtext">
                    This action cannot be undone. The survey and all its data will be permanently removed.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="indirect_method_modal_footer">
              <button 
                className="indirect_method_modal_button indirect_method_cancel_button"
                onClick={handleCancelDelete}
                disabled={isDeletingInProgress}
              >
                Cancel
              </button>
              <button 
                className="indirect_method_modal_button indirect_method_delete_button"
                onClick={handleConfirmDelete}
                disabled={isDeletingInProgress}
              >
                {isDeletingInProgress ? (
                  <>
                    <span className="indirect_method_loading_spinner"></span>
                    Deleting...
                  </>
                ) : (
                  <>
                    🗑️ Delete Survey
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Surveys Modal */}
      {isImportModalOpen && (
        <IndirectMethod_ImportSurveysModal
          onClose={handleImportModalClose}
          onUploadSuccess={handleImportSuccess}
          programName={programName}
          school={school}
        />
      )}
    </div>
  );
};

export default IndirectMethodSummary; 